import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { TRANSLATIONS, SUPPORTED_LOCALES, detectBrowserLocale } from "@/i18n/translations";

const I18nContext = createContext(null);

const STORAGE_KEY = "neuracom_locale";

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const saved = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
    return detectBrowserLocale();
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) next = "en";
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) { /* ignore */ }
  }, []);

  const t = useCallback(
    (path, vars = {}) => {
      const parts = path.split(".");
      let cur = TRANSLATIONS[locale];
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) {
          cur = cur[p];
        } else {
          cur = null;
          break;
        }
      }
      if (cur == null) {
        // Fallback to English
        let en = TRANSLATIONS.en;
        for (const p of parts) {
          if (en && typeof en === "object" && p in en) en = en[p];
          else { en = null; break; }
        }
        // Caller-provided default trumps the raw key fallback
        cur = en ?? (vars && typeof vars._default === "string" ? vars._default : path);
      }
      if (typeof cur === "string" && vars) {
        return cur.replace(/\{(\w+)\}/g, (_, k) =>
          k === "_default" ? "" : (vars[k] != null ? String(vars[k]) : `{${k}}`)
        );
      }
      return cur;
    },
    [locale]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
